import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/AdminDashboard.css";

const API_BASE_URL = "https://naxor.naxora.net";

export default function AdminHiddenAccounts() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [hiddenAccounts, setHiddenAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [search, setSearch] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Admin access required. Please login again.",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");

    window.location.href = "/login";
  };

  const getUserHideKey = (user = {}) => {
    return String(user.id || user.email || "").trim().toLowerCase();
  };

  const isHidden = (user = {}) => {
    const userKey = getUserHideKey(user);
    const userEmail = String(user.email || "").trim().toLowerCase();

    return hiddenAccounts.some((account) => {
      const accountKey = String(account.key || account.id || account.email || "")
        .trim()
        .toLowerCase();
      const accountEmail = String(account.email || "").trim().toLowerCase();

      return (
        (userKey && accountKey && userKey === accountKey) ||
        (userEmail && accountEmail && userEmail === accountEmail)
      );
    });
  };

  const fetchUsers = async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      method: "GET",
      headers: getHeaders(),
    });

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      await handleUnauthorized();
      return [];
    }

    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch users");
    }

    return Array.isArray(data.users) ? data.users : [];
  };

  const fetchHiddenAccounts = async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/hidden-accounts`, {
      method: "GET",
      headers: getHeaders(),
    });

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      await handleUnauthorized();
      return [];
    }

    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch hidden accounts");
    }

    return Array.isArray(data.hiddenAccounts) ? data.hiddenAccounts : [];
  };

  const loadPageData = async () => {
    try {
      setLoading(true);

      const [usersData, hiddenAccountsData] = await Promise.all([
        fetchUsers(),
        fetchHiddenAccounts(),
      ]);

      setUsers(usersData);
      setHiddenAccounts(hiddenAccountsData);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load data",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return users;

    return users.filter((user) => {
      return (
        String(user.name || "").toLowerCase().includes(keyword) ||
        String(user.email || "").toLowerCase().includes(keyword) ||
        String(user.referralCode || "").toLowerCase().includes(keyword)
      );
    });
  }, [users, search]);

  const hideAccount = async (user) => {
    const userKey = getUserHideKey(user);

    if (!userKey) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "User ID or email is missing.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Hide Account?",
      html: `
        <div style="text-align:left">
          <p>This account will be hidden from the main admin dashboard for all admins/devices.</p>
          <p style="margin-top:10px;color:#d4af37">Account:</p>
          <strong>${user.email || user.name || user.id}</strong>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Hide",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d4af37",
      cancelButtonColor: "#333333",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    if (!result.isConfirmed) return;

    try {
      setSavingId(userKey);

      const response = await fetch(`${API_BASE_URL}/api/admin/hidden-accounts`, {
        method: "POST",
        headers: getHeaders(true),
        body: JSON.stringify({
          account: {
            key: userKey,
            id: user.id || "",
            name: user.name || "",
            email: user.email || "",
          },
        }),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to hide account");
      }

      setHiddenAccounts(Array.isArray(data.hiddenAccounts) ? data.hiddenAccounts : []);

      Swal.fire({
        icon: "success",
        title: "Hidden",
        text: data.message || "Account hidden successfully.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to hide account",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSavingId("");
    }
  };

  const unhideAccount = async (user) => {
    const userKey = getUserHideKey(user);

    const result = await Swal.fire({
      icon: "question",
      title: "Unhide Account?",
      text: user.email || user.name || user.id || "Selected account",
      showCancelButton: true,
      confirmButtonText: "Unhide",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d4af37",
      cancelButtonColor: "#333333",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    if (!result.isConfirmed) return;

    try {
      setSavingId(userKey);

      const response = await fetch(`${API_BASE_URL}/api/admin/hidden-accounts`, {
        method: "DELETE",
        headers: getHeaders(true),
        body: JSON.stringify({
          account: {
            key: userKey,
            id: user.id || "",
            email: user.email || "",
          },
        }),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to unhide account");
      }

      setHiddenAccounts(Array.isArray(data.hiddenAccounts) ? data.hiddenAccounts : []);

      Swal.fire({
        icon: "success",
        title: "Unhidden",
        text: data.message || "Account is visible again.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to unhide account",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-wrapper">
        <div className="admin-topbar">
          <div>
            <h1>Hidden Accounts</h1>
            <p>Hide or unhide accounts from the main admin dashboard</p>
          </div>

          <div className="admin-topbar-actions">
            <button
              className="admin-refresh-btn"
              onClick={() => navigate("/admin")}
            >
              Back to Admin
            </button>

            <button className="admin-refresh-btn" onClick={loadPageData}>
              Refresh
            </button>
          </div>
        </div>

        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span>Total Users</span>
            <strong>{users.length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Hidden Accounts</span>
            <strong>{hiddenAccounts.length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Visible Accounts</span>
            <strong>{users.filter((user) => !isHidden(user)).length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Admins</span>
            <strong>{users.filter((user) => user.isAdmin).length}</strong>
          </div>
        </div>

        <div className="admin-users-card">
          <div className="admin-card-top">
            <h2>Account Visibility Control</h2>

            <input
              type="text"
              placeholder="Search by name, email, referral code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-search-input"
            />
          </div>

          {loading ? (
            <div className="admin-empty-state">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="admin-empty-state">No users found</div>
          ) : (
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Balance</th>
                    <th>Referral Code</th>
                    <th>Current Status</th>
                    <th>Visibility Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => {
                    const hidden = isHidden(user);
                    const userKey = getUserHideKey(user);
                    const isSaving = savingId === userKey;

                    return (
                      <tr key={user.id || user.email}>
                        <td>{user.name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>${Number(user.balance || 0).toFixed(2)}</td>
                        <td>{user.referralCode || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className={`admin-badge-btn ${
                              hidden ? "inactive" : "active"
                            }`}
                          >
                            {hidden ? "Hidden" : "Visible"}
                          </button>
                        </td>
                        <td>
                          {hidden ? (
                            <button
                              type="button"
                              className="admin-edit-btn"
                              disabled={isSaving}
                              onClick={() => unhideAccount(user)}
                            >
                              {isSaving ? "Saving..." : "Unhide"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="admin-data-btn warning"
                              disabled={isSaving}
                              onClick={() => hideAccount(user)}
                            >
                              {isSaving ? "Saving..." : "Hide"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}